import { useState, useRef } from "react"
import { UseQueryOptions, UseQueryResult, useQuery, useMutation } from "react-query"
import { APIEndpoints } from "../constants/api-endpoints"
import { HttpRequest } from "../services/_http"
import { QueryKeys } from "./_query_keys"

export const useCachePurgeOptions = ( filterParams?: any, options?: UseQueryOptions): UseQueryResult<any,any> => {
    const method   = 'GET'
    const queryKey = [QueryKeys.CACHE_PURGE, 'options', filterParams]
    
    /** To keeping old data until response received with new data */
    const [keepPreviousData, setkeepPreviousData] = useState(true) 
    
    return useQuery({
        queryKey,
        queryFn: () => HttpRequest({
            url: `${APIEndpoints.CachePurge}/options`,
            method,
            params: filterParams ?? {}
        }),
        refetchOnWindowFocus:false,
        keepPreviousData,
        onError: ()=>setkeepPreviousData(false),
        onSuccess: ()=>setkeepPreviousData(true),
        ...options
    })
}

export const usePurgeCache = (options?: any) => {
    
    const method = 'POST'
    
    return useMutation(({data}: {data: any})=> HttpRequest({
        url: `${APIEndpoints.CachePurge}`,
        method,
        data
    }), options)
}

export interface CloudFrontStatusResponse {
    status: 'InProgress' | 'Completed'
}

export interface CloudflareVerifyResult {
    url: string
    cacheStatus: 'HIT' | 'MISS' | 'UNKNOWN'
    headers?: {
        'cf-cache-status'?: string
    }
}

export interface CloudflareVerifyResponse {
    results: CloudflareVerifyResult[]
}

export const useCloudFrontStatus = (
    invalidationId: string | null | undefined,
    options?: UseQueryOptions
): UseQueryResult<any, any> => {
    const method = 'GET'
    const queryKey = [QueryKeys.CACHE_PURGE, 'cloudfront', 'status', invalidationId]
    
    const isEnabled = Boolean(invalidationId) && (options?.enabled !== false)
    
    return useQuery({
        queryKey,
        queryFn: async () => {
            const response = await HttpRequest({
                url: `${APIEndpoints.CachePurge}/cloudfront/status`,
                method,
                params: { invalidationId }
            })
            return response.data as CloudFrontStatusResponse
        },
        enabled: isEnabled,
        refetchInterval: (latestData: any) => {
            // Stop polling when status is "Completed"
            // Polling logic is fully encapsulated in the hook using latest query data
            // No component-side state manipulation needed - React Query handles everything
            if (!isEnabled) {
                return false
            }
            const data = latestData as CloudFrontStatusResponse | undefined
            if (data?.status === 'Completed') {
                return false // Stop polling when completed
            }
            // Continue polling every 5 seconds when status is "InProgress" or undefined
            return 5000
        },
        refetchOnWindowFocus: false,
        ...options
    })
}

export const useCloudflareVerify = (options?: any) => {
    const method = 'POST'
    
    return useMutation(({data}: {data: { urls: string[] }}) => HttpRequest({
        url: `${APIEndpoints.CachePurge}/verify`,
        method,
        data
    }), options)
}

export const useCloudflareVerifyStatus = (
    urls: string[] | null | undefined,
    options?: UseQueryOptions
): UseQueryResult<any, any> => {
    const method = 'POST'
    const queryKey = [QueryKeys.CACHE_PURGE, 'cloudflare', 'verify', urls]
    
    const isEnabled = Boolean(urls && urls.length > 0) && (options?.enabled !== false)
    
    // Track attempt count using ref (resets when queryKey changes)
    const attemptCountRef = useRef<number>(0)
    const previousUrlsRef = useRef<string[] | null | undefined>(null)
    
    // Reset attempt count when URLs change
    if (previousUrlsRef.current !== urls) {
        attemptCountRef.current = 0
        previousUrlsRef.current = urls
    }
    
    return useQuery({
        queryKey,
        queryFn: async () => {
            // Increment attempt count before each request
            attemptCountRef.current += 1
            const response = await HttpRequest({
                url: `${APIEndpoints.CachePurge}/verify`,
                method,
                data: { urls }
            })
            const data = response.data as CloudflareVerifyResponse
            // Include attempt count in response for tracking
            return {
                ...data,
                attemptCount: attemptCountRef.current
            }
        },
        enabled: isEnabled,
        refetchInterval: (latestData: any) => {
            // Stop polling logic is fully encapsulated in the hook using latest query data
            if (!isEnabled) {
                return false
            }
            
            const data = latestData as (CloudflareVerifyResponse & { attemptCount?: number }) | undefined
            const attemptCount = data?.attemptCount || attemptCountRef.current
            
            // Stop after 5 attempts total (1 initial + 4 retries)
            if (attemptCount >= 5) {
                return false
            }
            
            if (data?.results && data.results.length > 0) {
                // Check if all URLs have MISS status
                const allMiss = data.results.every((result: CloudflareVerifyResult) => 
                    result.cacheStatus === 'MISS'
                )
                if (allMiss) {
                    return false // Stop polling when all URLs are MISS
                }
                // Continue polling if any URL has HIT status (retry up to 4 more times)
                return 5000
            }
            // Continue polling if no data yet
            return 5000
        },
        refetchOnWindowFocus: false,
        ...options
    })
}


